import dayjs from 'dayjs';
import {
  AssemblyType,
  AssemblyTypeValue,
  BackflowAssemblyInfo,
  InstallationStatus,
  JobFormDataBackflow,
  RpCheck2Type,
  TestRepair,
  TestResult,
  TestResultRp,
} from '../../types/jobFormData';
import { ValidationStatus } from './form-builder';
import {
  AnyForm,
  emptyForm,
  formAvbAirGap,
  formDC,
  formRP,
  formSvbPvb,
  formType2Bypass,
} from './form-definitions';
import {
  assemblyTypeKey,
  isTestable,
  isValidMeterReading,
  meterReadingRequired,
  parseTestDate,
  validateTestDate,
  validateTestTimestamp,
} from './helpers';

export enum TestPosition {
  INITIAL = 'initial',
  REPAIR = 'repair',
  FINAL = 'final',
}
export type TestResultPosition = TestPosition.INITIAL | TestPosition.FINAL;

const testForms = {
  RP: formRP,
  RPDA: formRP,
  RPDA_2: formRP,
  DC: formDC,
  DCDA: formDC,
  DCDA_2: formDC,
  PVB: formSvbPvb,
  SVB: formSvbPvb,
  TYPE_2_BYPASS: formType2Bypass,
  AVB: formAvbAirGap,
  AIR_GAP: formAvbAirGap,
} as const;

export function initTestForm(
  test: TestResult,
  type: AssemblyTypeValue | undefined,
  rpCheck2Type: RpCheck2Type = 'USC10',
  inputValues?: any
): AnyForm {
  const formType = type === undefined ? undefined : assemblyTypeKey(type);
  if (!formType) return emptyForm;
  return testForms[formType].init(
    test as any,
    {
      formType,
      rpCheck2Type,
      check2Other:
        rpCheck2Type !== 'USC10' || (test as TestResultRp).check2 === 'other',
    },
    inputValues
  );
}

const testBaseKeys = [
  'type',
  'testDate',
  'timestamp',
  'passed',
  'latitude',
  'longitude',
];

// The values of a stored test that the assembly type's form can use.
// Tests keep answers from a previous assembly type (e.g. check2 = 'tight'
// after an RP is changed to a DC), so readers display this instead.
export function testValuesForType(
  test: TestResult,
  type: AssemblyTypeValue | undefined
): TestResult {
  const formType = type === undefined ? undefined : assemblyTypeKey(type);
  if (!formType) return test;
  return {
    ...Object.fromEntries(
      Object.entries(test).filter(([key]) => testBaseKeys.includes(key))
    ),
    ...testForms[formType].normalize(test),
  } as TestResult;
}

function formDataTestsInfo(
  fd: JobFormDataBackflow,
  testsArr: (TestResult | TestRepair)[]
) {
  const hasInitialTest = testsArr.length > 0;
  const hasRepairs = testsArr.length >= 2 && testsArr[1]!.type === 'repair';
  const hasFinalTest =
    (testsArr.length == 2 && !hasRepairs) ||
    (testsArr.length === 3 && hasRepairs);
  const unableToTest = !isTestable(fd);

  const hasTest = {
    [TestPosition.INITIAL]: hasInitialTest,
    [TestPosition.REPAIR]: hasRepairs,
    [TestPosition.FINAL]: hasFinalTest,
  };
  const tests = {
    [TestPosition.INITIAL]: hasInitialTest
      ? (testsArr[0] as TestResult)
      : undefined,
    [TestPosition.FINAL]: hasFinalTest
      ? (testsArr[testsArr.length - 1] as TestResult)
      : undefined,
    [TestPosition.REPAIR]: hasRepairs ? (testsArr[1] as TestRepair) : undefined,
  };

  return {
    hasTest,
    unableToTest,
    tests,
  };
}

// The assembly a validated form refers to: the edited/new assembly when the
// form has one, otherwise the job's original assembly (unless it was removed
// or explicitly replaced without new details yet).
function resolveAssembly(
  fd: JobFormDataBackflow,
  originalAssembly?: Partial<BackflowAssemblyInfo> | null
): Partial<BackflowAssemblyInfo> | undefined {
  if (fd.installationStatus === InstallationStatus.REMOVED) return undefined;
  if (fd.backflowAssembly) return fd.backflowAssembly;
  const status = fd.installationStatus ?? InstallationStatus.EXISTING;
  return status === InstallationStatus.EXISTING
    ? (originalAssembly ?? undefined)
    : undefined;
}

const validTestSequence = (tests: unknown[]): boolean => {
  if (tests.length > 3) return false;
  if (
    tests.some(
      (test) =>
        !test ||
        typeof test !== 'object' ||
        Array.isArray(test) ||
        ![undefined, null, 'repair'].includes((test as TestRepair).type)
    )
  ) {
    return false;
  }
  if (tests[0] && (tests[0] as TestRepair).type === 'repair') return false;
  if (tests.length === 3) {
    return (
      (tests[1] as TestRepair).type === 'repair' &&
      (tests[2] as TestRepair).type !== 'repair'
    );
  }
  return true;
};

const formStatus = (form: AnyForm) =>
  !form.completed
    ? ValidationStatus.INCOMPLETE
    : !form.passing
      ? ValidationStatus.FAIL
      : ValidationStatus.PASS;

export type EvaluateBackflowFormOptions = {
  rpCheck2Type?: RpCheck2Type;
  originalAssembly?: Partial<BackflowAssemblyInfo> | null;
  testInputValues?: { [which in TestResultPosition]?: any };
};

export type BackflowFormEvaluation = ReturnType<typeof evaluateBackflowForm>;

export function evaluateBackflowForm(
  fd: JobFormDataBackflow,
  options: EvaluateBackflowFormOptions = {}
) {
  const errors: string[] = [];
  const rpCheck2Type = options.rpCheck2Type ?? 'USC10';
  const rawTests = Array.isArray(fd.tests) ? fd.tests : [];
  const testSequenceValid =
    (fd.tests == null || Array.isArray(fd.tests)) &&
    validTestSequence(rawTests);
  const tests = testSequenceValid ? rawTests : [];
  const testsInfo = formDataTestsInfo(fd, tests);
  const { hasTest, unableToTest } = testsInfo;
  const hasInitialTest = hasTest[TestPosition.INITIAL];
  const hasFinalTest = hasTest[TestPosition.FINAL];

  const removed = fd.installationStatus === InstallationStatus.REMOVED;
  const assembly = resolveAssembly(
    fd,
    options.originalAssembly ?? fd.originalBackflowAssembly
  );
  const type = assembly?.type;

  if (!unableToTest && !testSequenceValid) {
    errors.push('Test results have an invalid sequence');
  }

  if (!removed) {
    if (!assembly) {
      errors.push('Assembly information is required');
    } else if (!type) {
      errors.push('Assembly type is required');
    } else {
      if (
        type !== AssemblyType.AVB &&
        type !== AssemblyType.AIR_GAP &&
        !assembly.modelId
      ) {
        errors.push('Assembly make and model are required');
      }
      if (
        assembly.makeName?.toString().trim().toUpperCase() === 'UNKNOWN' ||
        assembly.modelName?.toString().trim().toUpperCase() === 'UNKNOWN'
      ) {
        errors.push('Assembly make and/or model cannot be "Unknown"');
      }
    }
  }

  if (!fd.certificationNumber) {
    errors.push('Tester certification number is required');
  }

  if (!unableToTest) {
    if (!fd.gaugeId && !fd.gaugeNumber) {
      errors.push('Gauge information is required');
    } else if (!fd.gaugeCalibrated) {
      errors.push('Gauge calibration date is required');
    }

    if (!hasInitialTest && testSequenceValid) {
      errors.push('Test results are required');
    }

    tests.forEach((testAttempt) => {
      const dateErrors = validateTestDate(testAttempt.testDate);
      errors.push(...dateErrors);
      errors.push(...validateTestTimestamp(testAttempt.timestamp));

      if (testAttempt.type === 'repair') {
        if (!`${testAttempt.comment ?? ''}`.trim()) {
          errors.push('Repair comments are required');
        }
        return;
      }

      if (fd.gaugeCalibrated && testAttempt.testDate && !dateErrors.length) {
        const gaugeCalibrated = dayjs(fd.gaugeCalibrated, 'YYYY-MM-DD', true);
        if (!gaugeCalibrated.isValid()) {
          errors.push(
            `Gauge calibration date "${fd.gaugeCalibrated}" is invalid`
          );
        } else {
          const testDate = parseTestDate(testAttempt.testDate);
          const calibrationExpires = gaugeCalibrated.add(1, 'year');
          if (testDate.isAfter(calibrationExpires)) {
            errors.push(
              `Gauge calibration expired on ${calibrationExpires.format(
                'M/D/YYYY'
              )} before the test date ${testDate.format('M/D/YYYY')}`
            );
          }
        }
      }
    });

    if (
      type &&
      meterReadingRequired(type) &&
      !isValidMeterReading(fd.meterReading)
    ) {
      errors.push('Meter reading required');
    }
  } else {
    if (!`${fd.comments ?? ''}`.trim()) {
      errors.push('Comments are required');
    }
  }

  const testForm = (position: TestResultPosition): AnyForm => {
    const test = testsInfo.tests[position];
    return test
      ? initTestForm(
          test,
          type,
          rpCheck2Type,
          options.testInputValues?.[position]
        )
      : emptyForm;
  };
  const form = {
    [TestPosition.INITIAL]: testForm(TestPosition.INITIAL),
    [TestPosition.FINAL]: testForm(TestPosition.FINAL),
  };

  const valid =
    errors.length === 0 &&
    (unableToTest ||
      (hasInitialTest &&
        form.initial.completed &&
        (!hasFinalTest || form.final.completed)));

  const initialStatus =
    unableToTest || !hasInitialTest
      ? ValidationStatus.HIDE
      : formStatus(form.initial);
  const finalStatus = !hasFinalTest
    ? ValidationStatus.HIDE
    : formStatus(form.final);

  const combinedStatus = valid
    ? ValidationStatus.PASS
    : !hasInitialTest
      ? undefined
      : initialStatus === ValidationStatus.INCOMPLETE ||
          finalStatus === ValidationStatus.INCOMPLETE
        ? ValidationStatus.INCOMPLETE
        : initialStatus === ValidationStatus.FAIL ||
            finalStatus === ValidationStatus.FAIL
          ? ValidationStatus.FAIL
          : ValidationStatus.INCOMPLETE;

  const canHaveRepairs =
    (!unableToTest && initialStatus === ValidationStatus.FAIL) ||
    hasTest[TestPosition.REPAIR];
  const canHaveFinalTest =
    !unableToTest && initialStatus === ValidationStatus.FAIL;

  return {
    ...testsInfo,
    valid,
    assembly,
    form,
    combinedStatus,
    status: {
      [TestPosition.INITIAL]: initialStatus,
      [TestPosition.FINAL]: finalStatus,
    },
    canHaveRepairs,
    canHaveFinalTest,
    errors,
  };
}
